import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Layers, 
  ChevronDown, 
  ChevronRight, 
  Check,
  Radio,
  Shield,
  Camera,
  CloudLightning,
  Wind,
  Activity,
  Apple,
  AlertOctagon,
  Zap,
  Eye,
  EyeOff
} from 'lucide-react';

// Layer presets for quick access
const LAYER_PRESETS = [
  {
    name: 'Safety Mode',
    icon: 'Shield',
    description: 'Scanners + Sex Offenders',
    layers: { scanners: true, sexOffenders: true, cameras: false, weather: false, airQuality: false, diseaseOutbreaks: false, foodSafety: false, terrorism: false }
  },
  {
    name: 'Full Surveillance',
    icon: 'Eye',
    description: 'All security layers',
    layers: { scanners: true, sexOffenders: true, cameras: true, weather: false, airQuality: false, diseaseOutbreaks: false, foodSafety: false, terrorism: true }
  },
  {
    name: 'Environment',
    icon: 'CloudLightning',
    description: 'Weather + Air Quality',
    layers: { scanners: false, sexOffenders: false, cameras: false, weather: true, airQuality: true, diseaseOutbreaks: false, foodSafety: false, terrorism: false }
  },
  {
    name: 'Health Watch',
    icon: 'Activity',
    description: 'Disease + Food Safety',
    layers: { scanners: false, sexOffenders: false, cameras: false, weather: false, airQuality: false, diseaseOutbreaks: true, foodSafety: true, terrorism: false }
  },
  {
    name: 'All Layers',
    icon: 'Zap',
    description: 'Everything enabled',
    layers: { scanners: true, sexOffenders: true, cameras: true, weather: true, airQuality: true, diseaseOutbreaks: true, foodSafety: true, terrorism: true }
  },
  {
    name: 'Clear All',
    icon: 'EyeOff',
    description: 'Reset to scanners only',
    layers: { scanners: true, sexOffenders: false, cameras: false, weather: false, airQuality: false, diseaseOutbreaks: false, foodSafety: false, terrorism: false }
  }
];

const LAYER_CATEGORIES = [
  {
    name: 'Crime & Safety',
    color: 'red',
    layers: [
      { key: 'scanners', label: 'Scanners', desc: 'Audio feeds', icon: 'Radio' },
      { key: 'sexOffenders', label: 'Sex Offender Registry', desc: 'NSOPW data', icon: 'Shield', tier: 'basic' }
    ]
  },
  {
    name: 'Infrastructure',
    color: 'blue',
    layers: [
      { key: 'cameras', label: 'Traffic Cameras', desc: 'DOT feeds', icon: 'Camera' }
    ]
  },
  {
    name: 'Environment',
    color: 'amber',
    layers: [
      { key: 'weather', label: 'Weather Alerts', desc: 'NWS severe weather', icon: 'CloudLightning' },
      { key: 'airQuality', label: 'Air Quality', desc: 'EPA AQI index', icon: 'Wind' }
    ]
  },
  {
    name: 'Health Alerts',
    color: 'green',
    layers: [
      { key: 'diseaseOutbreaks', label: 'Disease Outbreaks', desc: 'CDC alerts', icon: 'Activity', tier: 'basic' },
      { key: 'foodSafety', label: 'Food Safety', desc: 'FDA recalls', icon: 'Apple', tier: 'basic' }
    ]
  },
  {
    name: 'Threat Alerts',
    color: 'orange',
    layers: [
      { key: 'terrorism', label: 'Terrorism Advisories', desc: 'DHS NTAS', icon: 'AlertOctagon', tier: 'basic' }
    ]
  }
];

const LAYERS_WITH_DETAILS = ['terrorism', 'diseaseOutbreaks', 'foodSafety', 'weather', 'sexOffenders', 'airQuality'];

const IconComponent = ({ iconName, isActive }) => {
  const className = `w-4 h-4 ${isActive ? 'text-teal-400' : 'text-slate-500'}`;
  
  switch (iconName) {
    case 'Radio': return <Radio className={className} />;
    case 'Shield': return <Shield className={className} />;
    case 'Camera': return <Camera className={className} />;
    case 'CloudLightning': return <CloudLightning className={className} />;
    case 'Wind': return <Wind className={className} />;
    case 'Activity': return <Activity className={className} />;
    case 'Apple': return <Apple className={className} />;
    case 'AlertOctagon': return <AlertOctagon className={className} />;
    case 'Zap': return <Zap className={className} />;
    case 'Eye': return <Eye className={className} />;
    case 'EyeOff': return <EyeOff className={className} />;
    default: return null;
  }
};

const DataOverlaysDropdown = ({ 
  mapLayers, 
  setMapLayers, 
  isPaid, 
  onShowLayerDetails 
}) => {
  const navigate = useNavigate();
  const activeLayerCount = Object.values(mapLayers).filter(Boolean).length;
  
  // Apply a preset
  const applyPreset = (preset) => {
    setMapLayers(preset.layers);
  };
  
  const handleLayerClick = (layer, e) => {
    e.preventDefault();
    const needsTier = layer.tier && !isPaid();
    
    if (needsTier) {
      navigate('/pricing');
      return;
    }
    
    // Toggle the layer AND open details if available
    setMapLayers(prev => ({ ...prev, [layer.key]: true }));
    
    if (LAYERS_WITH_DETAILS.includes(layer.key)) {
      onShowLayerDetails(layer);
    }
  };
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          className="hidden sm:flex items-center gap-2 bg-slate-900/80 border-slate-700 hover:bg-slate-800 hover:border-slate-600 text-slate-300"
          data-testid="layers-dropdown-trigger"
        >
          <Layers className="w-4 h-4 text-teal-400" />
          <span>Data Overlays</span>
          {activeLayerCount > 0 && (
            <Badge className="bg-teal-600 text-white text-[10px] px-1.5 py-0">{activeLayerCount}</Badge>
          )}
          <ChevronDown className="w-3 h-3 text-slate-500" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-80 glass-card border-slate-800 p-0 max-h-[70vh] overflow-y-auto">
        {/* Quick Presets */}
        <DropdownMenuLabel className="text-teal-400 text-xs uppercase tracking-wider px-3 py-2 flex items-center gap-2">
          <Zap className="w-3 h-3" />
          Quick Presets
        </DropdownMenuLabel>
        <div className="px-2 pb-2 grid grid-cols-3 gap-1">
          {LAYER_PRESETS.map((preset) => (
            <button
              key={preset.name}
              onClick={() => applyPreset(preset)}
              className="flex flex-col items-center gap-1 p-2 rounded-lg bg-slate-800/50 hover:bg-teal-600/20 hover:border-teal-500/50 border border-transparent transition-all text-center"
              title={preset.description}
              data-testid={`preset-${preset.name.toLowerCase().replace(/\s/g, '-')}`}
            >
              <IconComponent iconName={preset.icon} isActive={false} />
              <span className="text-[10px] text-slate-400 leading-tight">{preset.name}</span>
            </button>
          ))}
        </div>
        
        <DropdownMenuSeparator className="bg-slate-800" />
        
        {LAYER_CATEGORIES.map((category, catIdx) => (
          <div key={category.name}>
            {catIdx > 0 && <DropdownMenuSeparator className="bg-slate-800" />}
            <DropdownMenuLabel className="text-slate-500 text-xs uppercase tracking-wider px-3 py-2">
              {category.name}
            </DropdownMenuLabel>
            {category.layers.map((layer) => {
              const isActive = mapLayers[layer.key];
              const needsTier = layer.tier && !isPaid();
              const hasDetails = LAYERS_WITH_DETAILS.includes(layer.key);
              
              return (
                <DropdownMenuItem 
                  key={layer.key}
                  className={`cursor-pointer px-3 py-2 ${needsTier ? 'opacity-50' : ''}`}
                  onClick={(e) => handleLayerClick(layer, e)}
                  data-testid={`layer-toggle-${layer.key}`}
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        isActive ? 'bg-teal-600/20' : 'bg-slate-800'
                      }`}>
                        <IconComponent iconName={layer.icon} isActive={isActive} />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-slate-200">{layer.label}</p>
                        <p className="text-[10px] text-slate-500">{layer.desc}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {needsTier && (
                        <Badge className="bg-amber-600/20 text-amber-400 text-[9px]">PRO</Badge>
                      )}
                      {hasDetails && (
                        <ChevronRight className="w-4 h-4 text-slate-500" />
                      )}
                      {isActive && !hasDetails && (
                        <Check className="w-4 h-4 text-teal-400" />
                      )}
                    </div>
                  </div>
                </DropdownMenuItem>
              );
            })}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export { DataOverlaysDropdown, LAYER_CATEGORIES, LAYERS_WITH_DETAILS };
export default DataOverlaysDropdown;
